/** Cost cards read durable server state. YDoc changes only trigger a refresh. */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as Y from 'yjs'
import { z } from 'zod'
import { apiFetch, InsufficientSparksError } from '../api/client'
import {
  ApprovalRerunContextSchema,
  ApprovalSnapshotSchema,
  ConfirmApprovalsRequestSchema,
  type ApprovalRef,
  type ApprovalSnapshot,
  type RerunNodeContext,
} from '../generated/executionApprovals'

export interface ExecutionPlanEntry {
  nodeId: string
  type: string
  action?: string
  label: string
  sparks: number
  billingMode: 'paid' | 'free_trial'
  batchId: string
  status: 'pending' | 'confirmed' | 'cancelled'
  rerunContext?: RerunNodeContext
}

export interface ExecutionPlanBatch {
  batchId: string
  quote: ApprovalRef
  executions?: ApprovalSnapshot['executions']
  entries: ExecutionPlanEntry[]
  totalSparks: number
  payableSparks: number
  billingMode: 'paid' | 'free_trial'
  status: 'pending' | 'confirmed' | 'cancelled'
}

export interface UseExecutionPlanResult {
  batches: ExecutionPlanBatch[]
  hasPendingPlan: boolean
  executions: ApprovalSnapshot['executions']
  confirmBatch: (quotes: ApprovalRef[]) => Promise<void>
  cancelBatch: (batchId: string) => Promise<void>
}

const fundsError = z.object({ detail: z.object({
  code: z.literal('insufficient_sparks'),
  message: z.string().optional(),
}) })
const apiError = z.object({ detail: z.object({
  error: z.string().optional(), message: z.string().optional(),
}) })

const executionBillingHeaders = { 'X-Dreamwrite-Execution-Billing': 'payable-v1' }

export function useExecutionPlan(
  ydoc: Y.Doc | null,
  projectId: string,
  canvasId: string = 'agent',
  canViewCredits = false,
  authenticatedUserId: string | undefined = undefined,
): UseExecutionPlanResult {
  const [snapshot, setSnapshot] = useState<ApprovalSnapshot>({ approvals: [], executions: [] })
  const [contexts, setContexts] = useState<Record<string, Record<string, RerunNodeContext>>>({})
  const [contextRetryTick, setContextRetryTick] = useState(0)
  const attemptedContexts = useRef(new Map<string, number>())
  const canViewCreditsRef = useRef(canViewCredits)
  const previousCreditAccess = useRef(canViewCredits)
  const requestSequence = useRef(0)
  const scopeKey = `${authenticatedUserId ?? 'anonymous'}/${projectId}/${canvasId}`
  const currentScope = useRef(scopeKey)
  useLayoutEffect(() => {
    canViewCreditsRef.current = canViewCredits
    currentScope.current = scopeKey
  }, [canViewCredits, scopeKey])
  const base = `/projects/${encodeURIComponent(projectId)}/agentic/execution-plans`
  const query = `?canvas_id=${encodeURIComponent(canvasId)}`

  const refresh = useCallback(async () => {
    if (!projectId) return
    const scope = currentScope.current
    const sequence = ++requestSequence.current
    const response = await apiFetch(`${base}${query}`, { headers: executionBillingHeaders })
    if (!response.ok) throw new Error(`Approval state unavailable: ${response.status}`)
    const next = ApprovalSnapshotSchema.parse(await response.json())
    if (sequence === requestSequence.current && currentScope.current === scope) setSnapshot(next)
  }, [base, projectId, query])

  useEffect(() => {
    const sequenceCounter = requestSequence
    setSnapshot({ approvals: [], executions: [] })
    setContexts({})
    attemptedContexts.current.clear()
    const update = () => { void refresh().catch(console.error) }
    update()
    const timer = window.setInterval(update, 2000)
    const planMap = ydoc?.getMap('execution_plan')
    planMap?.observeDeep(update)
    return () => {
      ++sequenceCounter.current
      window.clearInterval(timer)
      planMap?.unobserveDeep(update)
    }
  }, [authenticatedUserId, refresh, ydoc])

  useEffect(() => {
    if (previousCreditAccess.current === canViewCredits) return
    previousCreditAccess.current = canViewCredits
    ++requestSequence.current
    setSnapshot({ approvals: [], executions: [] })
    setContexts({})
    attemptedContexts.current.clear()
    void refresh().catch(console.error)
  }, [canViewCredits, refresh])

  // Presentation context is fetched separately so the strict approval snapshot
  // and its quote hash are unchanged (including for old console bundles).
  const pendingContextKey = snapshot.approvals
    .filter((approval) => approval.status === 'pending')
    .map((approval) => `${approval.approval_id}:${approval.quote_hash}`).join('|')
  useEffect(() => {
    if (!pendingContextKey) return
    // A transient failure must be retried even when the pending set is stable.
    // Successful contexts are cached and skipped by the request effect.
    const timer = window.setInterval(() => setContextRetryTick((tick) => tick + 1), 30_000)
    return () => window.clearInterval(timer)
  }, [pendingContextKey])
  useEffect(() => {
    if (!projectId || !canViewCredits) return
    const scope = currentScope.current
    const pending = snapshot.approvals.filter((approval) => approval.status === 'pending')
    for (const approval of pending) {
      const cacheKey = `${scope}/${approval.approval_id}:${approval.quote_hash}`
      const lastAttempt = attemptedContexts.current.get(cacheKey)
      if (lastAttempt && Date.now() - lastAttempt < 30_000) continue
      attemptedContexts.current.set(cacheKey, Date.now())
      void apiFetch(`${base}/${encodeURIComponent(approval.approval_id)}/context${query}`)
        .then(async (response) => {
          // Collaborators can see a pending quote without owning its private
          // annotation. A 404 is definitive for this approval, not transient.
          if (response.status === 404) {
            attemptedContexts.current.set(cacheKey, Number.POSITIVE_INFINITY)
            return null
          }
          if (!response.ok) throw new Error(`Cost context unavailable: ${response.status}`)
          return ApprovalRerunContextSchema.parse(await response.json())
        })
        .then((context) => {
          if (context === null) return
          if (context.approval_id !== approval.approval_id || context.quote_hash !== approval.quote_hash) {
            throw new Error('Cost context is for a different approval quote')
          }
          // A newly pending approval must not discard an older card's in-flight
          // response. Scope is enough to prevent cross-project stale display.
          if (currentScope.current !== scope || !canViewCreditsRef.current) return
          attemptedContexts.current.set(cacheKey, Number.POSITIVE_INFINITY)
          console.info('rerun-cost-card:context_loaded', {
            approval_id: context.approval_id, quote_hash: context.quote_hash,
            rerun_nodes: context.nodes.filter((node) => node.display_action === 'rerun').map((node) => node.node_id),
          })
          setContexts((previous) => ({ ...previous, [cacheKey]: Object.fromEntries(
            context.nodes.map((node) => [node.node_id, node]),
          ) }))
        })
        .catch((error) => {
          // An annotation failure must leave the ordinary cost card usable.
          console.warn('Rerun cost context unavailable', error)
        })
    }
    // pendingContextKey is a stable representation of pending approvals; never
    // re-request context on every 2-second approval snapshot poll.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, canViewCredits, canvasId, contextRetryTick, pendingContextKey, projectId, query])

  const mutate = useCallback(async (path: string, body?: string) => {
    const scope = currentScope.current
    ++requestSequence.current
    const response = await apiFetch(`${base}${path}${query}`, {
      method: 'POST',
      headers: { ...executionBillingHeaders, 'Content-Type': 'application/json' },
      body,
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) {
      const funds = fundsError.safeParse(payload)
      if (response.status === 402 && funds.success) {
        throw new InsufficientSparksError(
          funds.data.detail.message ?? 'Insufficient credits',
        )
      }
      const error = apiError.safeParse(payload)
      // A missing/stale card is a failed confirmation. Never rewrite canvas
      // status or present HTTP 410 as a successful spending authorization.
      throw new Error(error.success
        ? error.data.detail.message ?? error.data.detail.error ?? `Approval failed: ${response.status}`
        : `Approval failed: ${response.status}`)
    }
    if (currentScope.current !== scope) return
    ++requestSequence.current
    setSnapshot(ApprovalSnapshotSchema.parse(payload))
  }, [base, query])

  const confirmBatch = useCallback(async (quotes: ApprovalRef[]) => {
    const request = ConfirmApprovalsRequestSchema.parse({ requests: quotes })
    await mutate('/confirm', JSON.stringify(request))
  }, [mutate])
  const cancelBatch = useCallback(async (batchId: string) => {
    await mutate(`/${encodeURIComponent(batchId)}/cancel`)
  }, [mutate])

  const batches = useMemo<ExecutionPlanBatch[]>(() => snapshot.approvals.map((approval) => {
    const status = approval.status === 'approved' ? 'confirmed'
      : approval.status === 'rejected' ? 'cancelled' : 'pending'
    return {
      batchId: approval.approval_id,
      quote: { approval_id: approval.approval_id, quote_version: approval.quote_version,
        quote_hash: approval.quote_hash },
      totalSparks: approval.nodes.reduce((sum, node) => sum + node.sparks_estimated, 0),
      payableSparks: approval.maximum_spend,
      billingMode: approval.billing_mode,
      executions: snapshot.executions.filter((execution) => execution.batch_id === approval.approval_id),
      status,
      entries: approval.nodes.map((node) => ({
        nodeId: node.node_id, type: node.type, action: node.action ?? undefined,
        label: node.label,
        sparks: node.sparks_estimated,
        billingMode: approval.billing_mode,
        batchId: approval.approval_id,
        status,
        rerunContext: contexts[`${authenticatedUserId ?? 'anonymous'}/${projectId}/${canvasId}/${approval.approval_id}:${approval.quote_hash}`]?.[node.node_id],
      })),
    }
  }), [authenticatedUserId, snapshot, contexts, projectId, canvasId])
  return { batches, hasPendingPlan: batches.some((b) => b.status === 'pending'),
    executions: snapshot.executions, confirmBatch, cancelBatch }
}
